import { useAuth } from "@via/hooks/useAuth";
import { useNotifications } from "@via/hooks/useNotifications";
import {
  useGetSavedAmazonCredentialsList,
  useGetSavedGoogleCredentialsList,
} from "@via/pages/data/data-sources/data-source-credentials.api";
import { BUILT_IN_ACCESS_OPTION_ID } from "@via/pages/data/constants";
import {
  useProjectList,
  useVmetaProjects,
} from "@via/pages/projects/projects.api";
import { useVmetaFields } from "@via/pages/projects/metadata/metadata.api";
import { SampleTrackerViewType } from "@via/pages/sample-trackers/sample-trackers-filter";
import {
  useCreateScheduler,
  useUpdateScheduler,
  useAccessibleRuns,
  useFetchRunConfig,
} from "@via/pages/runs/schedulers/schedulers.api";
import {
  DEFAULT_CREDENTIAL_OPTION,
  INTERVAL_OPTIONS,
  OVERWRITE_METHOD_OPTIONS,
  OVERWRITE_VALUE_TYPE_OPTIONS,
  type SchedulerFormValues,
} from "@via/pages/runs/schedulers/utils/helpers";
import type { SelectOption } from "@via/utils/interfaces";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import {
  ProjectViewType,
  type RunScheduler,
  type RunSchedulerOverwriteValueType,
} from "common";

const SCHEDULER_FORM_DEFAULTS: SchedulerFormValues = {
  name: "",
  enabled: true,
  projectId: null,
  templateRunId: null,
  sourceMethod: "sample-tracker",
  watchSourcePath: "",
  amazonCredentialId: BUILT_IN_ACCESS_OPTION_ID,
  googleCredentialId: BUILT_IN_ACCESS_OPTION_ID,
  sampleTrackerId: null,
  collectionId: null,
  watchPathField: null,
  statusField: null,
  readyValue: "",
  runConfigText: "{}",
  markerFileRelativePath: "",
  startDate: "",
  endDate: "",
  intervalMinutes: 15,
  overwriteRows: [],
};

function toDatetimeLocal(iso?: string): string {
  if (!iso) return "";
  const d = dayjs(iso);
  return d.isValid() ? d.format("YYYY-MM-DDTHH:mm") : "";
}

function getEditDefaults(scheduler: RunScheduler): SchedulerFormValues {
  const runCfg = scheduler.runConfigJson ?? {};
  return {
    name: scheduler.name ?? "",
    enabled: !!scheduler.enabled,
    projectId: scheduler.projectId ?? null,
    templateRunId: scheduler.templateRunId ?? null,
    sourceMethod: scheduler.sourceMethod ?? "sample-tracker",
    watchSourcePath: scheduler.watchPathSource ?? "",
    amazonCredentialId:
      scheduler.amazonCreId && Number(scheduler.amazonCreId) > 0
        ? Number(scheduler.amazonCreId)
        : BUILT_IN_ACCESS_OPTION_ID,
    googleCredentialId:
      scheduler.googleCreId && Number(scheduler.googleCreId) > 0
        ? Number(scheduler.googleCreId)
        : BUILT_IN_ACCESS_OPTION_ID,
    sampleTrackerId: scheduler.sampleTrackerId ?? null,
    collectionId: scheduler.sampleTrackerCollectionId ?? null,
    watchPathField: scheduler.watchPathField ?? null,
    statusField: scheduler.statusField ?? null,
    readyValue: scheduler.readyValue ?? "",
    runConfigText:
      typeof runCfg === "string" ? runCfg : JSON.stringify(runCfg, null, 2),
    markerFileRelativePath: scheduler.markerFileRelativePath ?? "",
    startDate: toDatetimeLocal(scheduler.startDate),
    endDate: toDatetimeLocal(scheduler.endDate),
    intervalMinutes: INTERVAL_OPTIONS.find(
      o => Number(o.value) === scheduler.intervalMinutes
    )
      ? scheduler.intervalMinutes
      : 15,
    overwriteRows: (scheduler.overwriteParameters ?? []).map(p => ({ ...p })),
  };
}

export function useSchedulerForm(
  open: boolean,
  mode: "create" | "edit",
  initialScheduler: RunScheduler | null
) {
  const { user } = useAuth();
  const { setNotification, NOTIFICATION } = useNotifications();

  const { mutateAsync: createScheduler, isPending: isCreatePending } =
    useCreateScheduler();
  const { mutateAsync: updateScheduler, isPending: isUpdatePending } =
    useUpdateScheduler();

  const { data: projectsResp, isLoading: isProjectsLoading } = useProjectList(
    0,
    1000,
    [{ id: "updatedAt", desc: true }],
    [{ id: "type", value: ProjectViewType.UserOwnedAndGroupShared }],
    open
  );

  const projectOptions = useMemo<SelectOption[]>(() => {
    const list = projectsResp?.data ?? [];
    return (Array.isArray(list) ? list : []).map((p: any) => ({
      label: `${p.name} (${p.id})`,
      value: p.id,
    }));
  }, [projectsResp]);

  const {
    control,
    setValue,
    getValues,
    trigger,
    reset,
    register,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<SchedulerFormValues>({
    mode: "onSubmit",
    defaultValues: SCHEDULER_FORM_DEFAULTS,
  });

  const {
    fields: overwriteFields,
    append: appendOverwriteRow,
    remove: removeOverwriteRow,
    replace: replaceOverwriteRows,
  } = useFieldArray({ control, name: "overwriteRows" });

  const watchedSourceMethod =
    useWatch({ control, name: "sourceMethod" }) ?? "sample-tracker";
  const watchedTemplateRunId = useWatch({ control, name: "templateRunId" });
  const watchedWatchSourcePath =
    useWatch({ control, name: "watchSourcePath" }) ?? "";
  const watchedCollectionId = useWatch({ control, name: "collectionId" });
  const watchedOverwriteRows =
    useWatch({ control, name: "overwriteRows" }) ?? [];

  const [trackerView, setTrackerView] = useState<SampleTrackerViewType>(
    SampleTrackerViewType.My
  );
  const [datasetTargetView, setDatasetTargetView] =
    useState<SampleTrackerViewType>(SampleTrackerViewType.My);
  const didAutoSetDatasetTargetViewRef = useRef(false);

  const {
    mutateAsync: fetchRunConfig,
    isPending: isLoadingConfig,
    reset: resetFetchRunConfig,
  } = useFetchRunConfig();

  const { data: accessibleRunsResp, isLoading: isRunsLoading } =
    useAccessibleRuns(100, 0, open);

  const { data: savedAmazonCredentials = [] } =
    useGetSavedAmazonCredentialsList(open);
  const { data: savedGoogleCredentials = [] } =
    useGetSavedGoogleCredentialsList(open);

  const amazonCredentialOptions = useMemo<SelectOption[]>(() => {
    return [
      DEFAULT_CREDENTIAL_OPTION,
      ...savedAmazonCredentials.map((c: any) => ({
        label: `${c.name ?? "Credential"} (${c.id})`,
        value: c.id,
      })),
    ];
  }, [savedAmazonCredentials]);

  const googleCredentialOptions = useMemo<SelectOption[]>(() => {
    return [
      DEFAULT_CREDENTIAL_OPTION,
      ...savedGoogleCredentials.map((c: any) => ({
        label: `${c.name ?? "Credential"} (${c.id})`,
        value: c.id,
      })),
    ];
  }, [savedGoogleCredentials]);

  const runOptions = useMemo<SelectOption[]>(() => {
    const runs = accessibleRunsResp?.data ?? [];
    return runs.map((r: any) => ({
      label: `${r.name} (${r.id}) • Project (${r.projectId})`,
      value: r.id,
    }));
  }, [accessibleRunsResp?.data]);

  const selectedCollectionIdForOverwrites =
    watchedCollectionId?.toString() ?? "";

  const { data: overwriteFieldsResp, isLoading: isOverwriteFieldsLoading } =
    useVmetaFields(
      0,
      200,
      [{ id: "name", desc: false }],
      selectedCollectionIdForOverwrites
        ? [{ id: "collectionID", value: selectedCollectionIdForOverwrites }]
        : []
    );

  const overwriteFieldOptions = useMemo<SelectOption[]>(() => {
    const list = (overwriteFieldsResp as any)?.data ?? [];
    return (Array.isArray(list) ? list : []).map((f: any) => ({
      label: `${f.name}`,
      value: f.name,
    }));
  }, [overwriteFieldsResp]);

  const shouldLoadVmetaProjects =
    open &&
    (watchedSourceMethod === "sample-tracker" ||
      watchedOverwriteRows.some(
        r => (r.method ?? "replace") === "save-as-dataset"
      ));

  const { data: vmetaMyProjectsResp, isLoading: isVmetaMyProjectsLoading } =
    useVmetaProjects(
      0,
      300,
      [{ id: "updatedAt", desc: true }],
      [],
      shouldLoadVmetaProjects,
      SampleTrackerViewType.My,
      user?.vmeta?.userId
    );

  const {
    data: vmetaSharedProjectsResp,
    isLoading: isVmetaSharedProjectsLoading,
  } = useVmetaProjects(
    0,
    300,
    [{ id: "updatedAt", desc: true }],
    [],
    shouldLoadVmetaProjects,
    SampleTrackerViewType.Shared,
    user?.vmeta?.userId
  );

  const vmetaMyCanvasOptions = useMemo<SelectOption[]>(() => {
    const list = (vmetaMyProjectsResp as any)?.data ?? [];
    return (Array.isArray(list) ? list : [])
      .map((t: any) => ({
        label: `${t?.name ?? "Tracker"}`,
        value: String(t?._id ?? ""),
      }))
      .filter(o => !!o.value);
  }, [vmetaMyProjectsResp]);

  const vmetaSharedCanvasOptions = useMemo<SelectOption[]>(() => {
    const list = (vmetaSharedProjectsResp as any)?.data ?? [];
    return (Array.isArray(list) ? list : [])
      .map((t: any) => ({
        label: `${t?.name ?? "Tracker"}`,
        value: String(t?._id ?? ""),
      }))
      .filter(o => !!o.value);
  }, [vmetaSharedProjectsResp]);

  const vmetaCanvasOptions = useMemo<SelectOption[]>(() => {
    return datasetTargetView === SampleTrackerViewType.Shared
      ? vmetaSharedCanvasOptions
      : vmetaMyCanvasOptions;
  }, [datasetTargetView, vmetaMyCanvasOptions, vmetaSharedCanvasOptions]);

  const isVmetaCanvasLoading =
    isVmetaMyProjectsLoading || isVmetaSharedProjectsLoading;

  useEffect(() => {
    if (!open) return;
    if (mode !== "edit") return;
    if (didAutoSetDatasetTargetViewRef.current) return;
    if (datasetTargetView !== SampleTrackerViewType.My) return;
    if (
      vmetaMyCanvasOptions.length === 0 &&
      vmetaSharedCanvasOptions.length === 0
    )
      return;

    const anySelectedId = watchedOverwriteRows
      .map(r => String(r.samplesheetTargetCanvasId ?? "").trim())
      .find(Boolean);
    if (!anySelectedId) return;

    const inMy = vmetaMyCanvasOptions.some(
      o => String(o.value) === anySelectedId
    );
    const inShared = vmetaSharedCanvasOptions.some(
      o => String(o.value) === anySelectedId
    );
    if (!inMy && inShared) {
      setDatasetTargetView(SampleTrackerViewType.Shared);
    }
    didAutoSetDatasetTargetViewRef.current = true;
  }, [
    open,
    mode,
    datasetTargetView,
    watchedOverwriteRows,
    vmetaMyCanvasOptions,
    vmetaSharedCanvasOptions,
  ]);

  useEffect(() => {
    if (!open) return;
    resetFetchRunConfig();
    setDatasetTargetView(SampleTrackerViewType.My);
    didAutoSetDatasetTargetViewRef.current = false;
    setTrackerView(SampleTrackerViewType.My);

    if (mode === "edit" && initialScheduler) {
      reset(getEditDefaults(initialScheduler));
      return;
    }

    reset(SCHEDULER_FORM_DEFAULTS);
  }, [open]);

  const overwriteRowIssues = useMemo(() => {
    const issues: Array<{ index: number; missing: string[] }> = [];

    watchedOverwriteRows.forEach((r, idx) => {
      const missing: string[] = [];

      if (!r.configParameter?.trim()) missing.push("Config parameter");
      if (!r.method) missing.push("Method");

      const method = (r.method ?? "replace") as string;
      if (method === "save-as-dataset") {
        if (!String(r.samplesheetTargetCanvasId ?? "").trim()) {
          missing.push("Target sample tracker");
        }
        // NOTE: dataset name field is optional; backend can derive a name if unset.
      }

      const effectiveValueType: RunSchedulerOverwriteValueType =
        watchedSourceMethod === "sample-tracker"
          ? "sample-tracker-field"
          : (r.valueType as RunSchedulerOverwriteValueType);

      switch (effectiveValueType) {
        case "sample-tracker-field": {
          if (!r.sampleTrackerField?.trim()) {
            missing.push("Value from tracker field");
          }

          if (
            watchedSourceMethod === "sample-tracker" &&
            method === "save-as-dataset"
          ) {
            const loc = (r.samplesheetFileLocation ??
              "run-environment") as string;
            if (loc === "s3") {
              if (
                r.samplesheetAmazonCreId !== undefined &&
                Number(r.samplesheetAmazonCreId) !==
                  BUILT_IN_ACCESS_OPTION_ID &&
                Number.isNaN(Number(r.samplesheetAmazonCreId))
              ) {
                missing.push("AWS credential");
              }
            }
            if (loc === "gcs") {
              if (
                r.samplesheetGoogleCreId !== undefined &&
                Number(r.samplesheetGoogleCreId) !==
                  BUILT_IN_ACCESS_OPTION_ID &&
                Number.isNaN(Number(r.samplesheetGoogleCreId))
              ) {
                missing.push("GCP credential");
              }
            }
          }
          break;
        }
        case "relative-path":
          if (!r.relativePath?.trim()) missing.push("Relative path");
          break;
        case "json-key-from-relative-path":
          if (!r.jsonFileRelativePath?.trim()) missing.push("JSON file path");
          if (!r.jsonKeyDotPath?.trim()) missing.push("JSON key dot-path");
          break;
        case "derive-from-source-path":
          if (!r.sourcePathDerivation?.trim()) missing.push("Derivation");
          break;
        default:
          missing.push("Value");
          break;
      }

      if (missing.length > 0) issues.push({ index: idx, missing });
    });

    return issues;
  }, [watchedOverwriteRows, watchedSourceMethod]);

  const areOverwriteRowsValid = overwriteRowIssues.length === 0;

  const overwriteRowsValidationMessage = useMemo(() => {
    if (overwriteRowIssues.length === 0) return "";
    const lines = overwriteRowIssues.slice(0, 3).map(i => {
      const uniq = Array.from(new Set(i.missing));
      return `Row ${i.index + 1}: missing ${uniq.join(", ")}`;
    });
    const suffix =
      overwriteRowIssues.length > 3
        ? ` (+${overwriteRowIssues.length - 3} more)`
        : "";
    return `${lines.join(" · ")}${suffix}`;
  }, [overwriteRowIssues]);

  const overwriteValueTypeOptions = useMemo(() => {
    const allowed =
      watchedSourceMethod === "watch-path"
        ? new Set<RunSchedulerOverwriteValueType>([
            "relative-path",
            "json-key-from-relative-path",
            "derive-from-source-path",
          ])
        : new Set<RunSchedulerOverwriteValueType>(["sample-tracker-field"]);

    return OVERWRITE_VALUE_TYPE_OPTIONS.filter(opt => allowed.has(opt.value));
  }, [watchedSourceMethod]);

  const overwriteMethodOptions = useMemo(() => {
    return watchedSourceMethod === "watch-path"
      ? OVERWRITE_METHOD_OPTIONS
      : OVERWRITE_METHOD_OPTIONS.filter(
          opt => opt.value === "replace" || opt.value === "save-as-dataset"
        );
  }, [watchedSourceMethod]);

  useEffect(() => {
    if (!open) return;
    const currentRows = getValues("overwriteRows");
    if (currentRows.length === 0) return;

    const allowed =
      watchedSourceMethod === "watch-path"
        ? new Set<RunSchedulerOverwriteValueType>([
            "relative-path",
            "json-key-from-relative-path",
            "derive-from-source-path",
          ])
        : new Set<RunSchedulerOverwriteValueType>(["sample-tracker-field"]);

    const updated = currentRows.map(r => {
      if (watchedSourceMethod === "sample-tracker") {
        return {
          ...r,
          method:
            r.method === "append-suffix" || !r.method ? "replace" : r.method,
          valueType: "sample-tracker-field" as const,
          textValue: "",
          relativePath: "",
          jsonFileRelativePath: "",
          jsonKeyDotPath: "",
          sourcePathDerivation: undefined,
        };
      }

      if (allowed.has(r.valueType)) return r;
      const fallback = "derive-from-source-path" as const;
      return {
        ...r,
        method:
          r.method === "append-suffix"
            ? ("replace" as const)
            : (r.method ?? ("replace" as const)),
        valueType: fallback,
        textValue: "",
        sampleTrackerField: "",
        relativePath: "",
        jsonFileRelativePath: "",
        jsonKeyDotPath: "",
        sourcePathDerivation: "full" as const,
      };
    });
    replaceOverwriteRows(updated);
  }, [watchedSourceMethod]);

  const loadTemplateConfig = async () => {
    const templateRunId = getValues("templateRunId");
    if (!templateRunId) return;
    const json = await fetchRunConfig({ runId: String(templateRunId) });
    setValue(
      "runConfigText",
      typeof json === "string" ? json : JSON.stringify(json, null, 2)
    );
  };

  return {
    control,
    register,
    setValue,
    getValues,
    trigger,
    clearErrors,
    setError,
    errors,

    overwriteFields,
    appendOverwriteRow,
    removeOverwriteRow,

    watchedSourceMethod,
    watchedTemplateRunId,
    watchedWatchSourcePath,
    watchedOverwriteRows,

    trackerView,
    setTrackerView,
    datasetTargetView,
    setDatasetTargetView,

    projectOptions,
    runOptions,
    amazonCredentialOptions,
    googleCredentialOptions,
    overwriteFieldOptions,
    vmetaCanvasOptions,
    overwriteValueTypeOptions,
    overwriteMethodOptions,

    isProjectsLoading,
    isRunsLoading,
    isLoadingConfig,
    isOverwriteFieldsLoading,
    isVmetaCanvasLoading,

    areOverwriteRowsValid,
    overwriteRowsValidationMessage,
    selectedCollectionIdForOverwrites,

    mongoUserId: user?.vmeta?.userId,

    createScheduler,
    isCreatePending,
    updateScheduler,
    isUpdatePending,

    setNotification,
    NOTIFICATION,

    loadTemplateConfig,
  };
}
